// <donut-chart> — the labour × materials split, with a two-line centre
// label drawn by a per-chart plugin. Same contract as <line-chart>: JSON
// payload script + sized canvas wrapper, element only draws. The legend
// beside it is server-rendered HTML.
//
//   <donut-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative size-40"><canvas></canvas></div>
//   </donut-chart>

import { Chart, type Plugin } from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	gbp,
	onThemeChange,
	readPayload,
} from "./chart-theme";

type Payload = {
	center: { value: string; label: string };
	slices: { label: string; value: number; color: string }[]; // value: pence
};

// Built per chart so the closure carries this donut's text; colours resolve
// at draw time so a theme switch repaints correctly on rebuild.
function centerText(center: Payload["center"]): Plugin<"doughnut"> {
	return {
		id: "donutCenterText",
		afterDatasetsDraw(chart) {
			const { ctx, chartArea } = chart;
			const x = (chartArea.left + chartArea.right) / 2;
			const y = (chartArea.top + chartArea.bottom) / 2;
			const family = Chart.defaults.font.family ?? "sans-serif";

			ctx.save();
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillStyle = chartColor("--color-ink");
			ctx.font = `700 17px ${family}`;
			ctx.fillText(center.value, x, y - 8);
			ctx.fillStyle = chartColor("--color-ink-faint");
			ctx.font = `600 10px ${family}`;
			ctx.fillText(center.label.toUpperCase(), x, y + 11);
			ctx.restore();
		},
	};
}

class DonutChart extends HTMLElement {
	#abort: AbortController | null = null;
	#chart: Chart<"doughnut"> | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();

		this.#render();
		onThemeChange(this.#abort.signal, () => this.#render());
	}

	disconnectedCallback() {
		this.#abort?.abort();
		this.#chart?.destroy();
		this.#chart = null;
	}

	#render() {
		ensureChartTheme();
		const payload = readPayload<Payload>(this, "donut-chart");

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "donut-chart"), {
			type: "doughnut",
			data: {
				labels: payload.slices.map((slice) => slice.label),
				datasets: [
					{
						data: payload.slices.map((slice) => slice.value),
						backgroundColor: payload.slices.map((slice) =>
							chartColor(slice.color),
						),
						// Hairline gaps between slices, cut in the card surface colour.
						borderColor: chartColor("--color-panel"),
						borderWidth: 2,
						hoverOffset: 5,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: "72%",
				// Headroom for hoverOffset so lifted slices don't clip.
				layout: { padding: 5 },
				plugins: {
					tooltip: {
						callbacks: {
							label: (item) => ` ${item.label}: ${gbp(item.parsed)}`,
						},
					},
				},
			},
			plugins: [centerText(payload.center)],
		});
	}
}

if (!customElements.get("donut-chart")) {
	customElements.define("donut-chart", DonutChart);
}
